import { z } from "zod";
import { SharedApiError, normalizeApiBaseUrl, type FetchLike } from "./shared";

export const userLookupQuerySchema = z.object({
  emails: z.string().min(1),
  workspace_id: z.string().uuid().optional(),
});

export const userLookupItemSchema = z.object({
  email: z.string().email(),
  id: z.string().min(1),
  name: z.string(),
  avatar_url: z.string().nullable().optional(),
  in_workspace: z.boolean().optional(),
});

export const userLookupSuccessSchema = z.object({
  users: z.array(userLookupItemSchema),
});

export type UserLookupItem = z.infer<typeof userLookupItemSchema>;
export type UserLookupSuccess = z.infer<typeof userLookupSuccessSchema>;

export function userLookupPath(): string {
  return "/api/users/lookup";
}

function buildAuthHeaders(accessToken?: string): Record<string, string> {
  const headers: Record<string, string> = {};
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }
  return headers;
}

export async function userLookupRequest({
  apiBaseUrl,
  accessToken,
  emails,
  workspaceId,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken?: string;
  emails: string[];
  workspaceId?: string;
  fetchImpl?: FetchLike;
}): Promise<UserLookupSuccess> {
  const fetchFn = fetchImpl ?? fetch;
  const params = new URLSearchParams({ emails: emails.join(",") });
  if (workspaceId) {
    params.set("workspace_id", workspaceId);
  }

  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${userLookupPath()}?${params}`,
    {
      method: "GET",
      headers: buildAuthHeaders(accessToken),
    },
  );

  if (!response.ok) {
    const payload =
      typeof response.json === "function"
        ? await response.json().catch(() => null)
        : null;
    const msg =
      payload && typeof payload.error === "string"
        ? payload.error
        : "Failed to look up users";
    throw new SharedApiError(msg, response.status);
  }

  const payload = await response.json().catch(() => null);
  const parsed = userLookupSuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError("Invalid user lookup response", 500, {
      issues: parsed.error.issues,
    });
  }
  return parsed.data;
}
